<template>
  <div>
    <EntitySearchPopover
      itemKey="id"
      :allowEmptySelection="allowEmptySelection"
      :allowSearchScopeChange="allowSearchScopeChange"
      :blacklist="blacklist"
      :disabled="disabled"
      :items="allItems"
      :loading="isLoading"
      :localFilterFields="['title']"
      :localFilter="localSearch"
      :matchingItems="matchingItems"
      :maxSelectable="maxSelectableAccounts"
      :multiple="multiple"
      :open="open"
      :placeholder="$t('common.search')"
      :resultsHeaderText="$t('assignee_selector.user_other')"
      :searchScope="searchScope"
      :selectedItems="selectedAccounts"
      :showAdvancedOption="showAdvancedOption"
      :zIndex="zIndex"
      @clearAll="onClearAll"
      @limitExceeded="handleLimitExceeded"
      @openChange="open = $event"
      @remove="onRemove"
      @search="searchItems"
      @searchScopeChange="handleSearchScopeChange"
      @select="onSelect"
      @showAdvancedSearch="handleOpenAdvanceModal"
    >
      <template v-slot:default>
        <slot></slot>
      </template>

      <template v-slot:item="{ item, searchTerm, isSelected }">
        <AccountListItem
          v-if="!item.isTeam"
          class="pl-1 pr-3 py-0"
          :account="item"
          :searchTerm="searchTerm"
          :selected="isSelected"
          @click="isSelected ? onRemove(item) : onSelect(item)"
        />
        <TeamListItem
          v-else
          class="pl-1 pr-3 py-0"
          :team="item"
          :searchTerm="searchTerm"
          @click="onSelect"
        />
      </template>

      <template v-slot:selectedItem="{ item, searchTerm }">
        <AccountListItem
          class="pl-1 pr-3 py-0"
          :account="item"
          :searchTerm="searchTerm"
          :selected="multiple || allowEmptySelection"
          @click="allowEmptySelection || multiple ? onRemove(item) : null"
        />
      </template>
    </EntitySearchPopover>

    <AdvancedAssigneeSelectorModal
      v-if="showAdvancedModal"
      :blacklist="blacklist"
      :filters="filters"
      :includeTeams="includeTeams"
      :initialAccounts="selectedAccounts"
      :maxSelectableAccounts="maxSelectableAccounts"
      :show="showAdvancedModal"
      @close="showAdvancedModal = false"
      @select="handleSelectFromAdvancedModal"
      @cacheUpdate="addAccountsToCache($event)"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'

import api from '@/api'
import constants from '@/constants.js'
import mixin from '@/mixins/mixin.vue'
import { fuseSearch } from '@/lib/fuseSearch'

import EntitySearchPopover from '@/components/EntitySearchPopover.vue'
import AccountListItem from './AccountListItem.vue'
import TeamListItem from './TeamListItem.vue'
import AdvancedAssigneeSelectorModal from './AdvancedAssigneeSelectorModal.vue'

const ITEMS_PER_PAGE = 30

export default {
  name: 'AssigneesSearchPopover',

  mixins: [mixin],

  components: {
    EntitySearchPopover,
    AccountListItem,
    TeamListItem,
    AdvancedAssigneeSelectorModal,
  },

  props: {
    allowSearchScopeChange: {
      type: Boolean,
      default: true,
    },
    availableLocalAccounts: {
      type: Array,
      default: () => [],
    },
    disabled: Boolean,
    includeTeams: Boolean,
    maxSelectableAccounts: {
      type: Number,
      default: null,
    },
    localSearch: Boolean,
    multiple: Boolean,
    selectedAccountIds: {
      type: Array,
      default: () => [],
    },
    showAdvancedOption: {
      type: Boolean,
      default: false,
    },
    blacklist: {
      type: Array,
      default: () => [],
    },
    filters: {
      type: Object,
      default: () => {},
    },
    allowEmptySelection: {
      type: Boolean,
      default: true,
    },
    includeCurrentAccountAtTop: {
      type: Boolean,
      default: true,
    },
    zIndex: { type: Number, default: 203 },
    targetOrganizationId: {
      type: String,
      default: null,
    },
    initialSearchScope: {
      type: String,
      default: constants.SEARCH_SCOPES.site,
    },
  },

  data() {
    return {
      areThereMoreInitialResults: false,
      availableAccounts: [],
      availableTeams: [],
      cachedAccounts: {},
      initialAccounts: [],
      initialLoadExecuted: false,
      initialLoadFinished: false,
      initialPageToLoad: 0,
      isLoading: false,
      open: false,
      searchScope: this.initialSearchScope,
      searchTerm: '',
      showAdvancedModal: false,
    }
  },

  watch: {
    availableLocalAccounts(newAccounts) {
      this.cachedAccounts = {}
      this.availableAccounts = newAccounts
      this.addAccountsToCache(newAccounts)
    },

    availableAccounts(newAccounts) {
      this.addAccountsToCache(newAccounts)
    },

    availableTeams(newTeams) {
      const teamAccounts = newTeams.reduce((result, team) => {
        return [...result, ...team.leaders, ...team.members]
      }, [])

      this.addAccountsToCache(teamAccounts)
    },

    initialAccounts(newAccounts) {
      this.$emit('initialLoad', this.parseAccounts(newAccounts))
      this.addAccountsToCache(newAccounts)
    },

    selectedAccountIds: {
      immediate: true,
      handler(value) {
        if (
          !this.initialLoadExecuted &&
          Boolean(value.length) &&
          !this.localSearch
        ) {
          this.initialLoadExecuted = true
          this.getInitialAccounts()
        }
      },
    },

    selectedAccounts(newVal, oldVal) {
      if (!this.multiple) return
      if (
        _.differenceBy(newVal, oldVal, 'id').length ||
        _.differenceBy(oldVal, newVal, 'id').length
      ) {
        this.$emit('change', {
          ids: this.selectedAccountIds,
          items: _.compact(newVal),
        })
      }
    },

    filters: {
      handler(newVal, oldVal) {
        if (_.isEqual(newVal, oldVal)) return

        this.cachedAccounts = {}
        this.searchItems()
      },
    },

    open(isOpening) {
      if (!this.initialLoadFinished && isOpening) {
        this.initialLoadFinished = true
        this.searchItems()
      }
    },
  },

  computed: {
    ...mapGetters(['account', 'organizationId']),

    allItems() {
      const baseAccounts = this.parseAccounts(
        _.compact(
          this.blacklist.length
            ? this.availableAccounts.filter(
                a => !this.blacklist.includes(a.id)
              )
            : this.availableAccounts
        )
      )

      const accountsWithCurrent = this.includeCurrentAccountAtTop
        ? _.uniqBy([this.account, ...baseAccounts], 'id')
        : _.uniqBy(baseAccounts, 'id')

      const teams = this.multiple ? this.parseTeams(this.availableTeams) : []
      const merged = _.uniqBy([...accountsWithCurrent, ...teams], 'id')

      return _.orderBy(merged, it => (it.title || '').toLowerCase(), 'asc')
    },

    selectedAccounts() {
      if (!this.multiple) {
        if (!this.selectedAccountIds.length) return []

        const selectedAccount = this.cachedAccounts[this.selectedAccountIds[0]]
        return selectedAccount ? [selectedAccount] : []
      }

      const selectedAccounts = _.orderBy(
        _.compact(
          this.selectedAccountIds.map(
            accountId => this.cachedAccounts?.[accountId] ?? null
          )
        ),
        item => item.title.toLowerCase(),
        'asc'
      )

      if (this.selectedAccountIds.includes(this.account.id)) {
        return _.uniqBy([this.account, ...selectedAccounts], 'id')
      }

      return selectedAccounts
    },

    unselectedItems() {
      const initialAccounts = this.includeCurrentAccountAtTop
        ? [this.account, ...this.availableAccounts]
        : this.availableAccounts

      const accounts = _.differenceBy(
        _.uniqBy(initialAccounts, 'id'),
        this.selectedAccounts,
        'id'
      )

      const accountsOmitingBlacklist = this.blacklist.length
        ? accounts.filter(account => !this.blacklist.includes(account.id))
        : accounts

      const parsedAccounts = this.parseAccounts(
        _.compact(accountsOmitingBlacklist)
      )
      const topScoreAccounts = parsedAccounts.slice(0, 5)
      const mergedAccounts = _.flatten(
        _.zip(
          parsedAccounts.slice(5),
          this.multiple ? this.parseTeams(this.availableTeams) : []
        )
      )
      return _.compact([...topScoreAccounts, ...mergedAccounts])
    },

    matchingItems() {
      const accountsOmitingBlacklist = this.blacklist.length
        ? this.availableAccounts.filter(
            account => !this.blacklist.includes(account.id)
          )
        : this.availableAccounts

      const parsedAccounts = this.parseAccounts(
        _.compact(accountsOmitingBlacklist)
      )
      const topScoreAccounts = parsedAccounts.slice(0, 5)
      const mergedAccounts = _.flatten(
        _.zip(
          parsedAccounts.slice(5),
          this.multiple ? this.parseTeams(this.availableTeams) : []
        )
      )
      const matchingItems = _.compact([...topScoreAccounts, ...mergedAccounts])
      const selectedAccountIdsSet = new Set(this.selectedAccountIds)
      return matchingItems.map(item => {
        return {
          ...item,
          isSelected:
            selectedAccountIdsSet.has(item.id) &&
            (this.multiple || this.allowEmptySelection),
        }
      })
    },

    localAccountsParsed() {
      return this.parseAccounts(this.availableLocalAccounts)
    },

    organizationIdToUse() {
      return this.targetOrganizationId || this.organizationId
    },
  },

  methods: {
    handleLimitExceeded({ limit }) {
      this.showError(
        this.$t('common.selection_limit_error', {
          selectionLimit: limit,
          type: this.$t('common.users.user_other'),
        })
      )
    },

    async getInitialAccounts() {
      try {
        const ids = _.compact(this.selectedAccountIds)
        if (!ids.length) return

        this.$emit('loading', true)
        const from = ITEMS_PER_PAGE * this.initialPageToLoad
        const response = await api.account.getAccounts(
          this.organizationIdToUse,
          {
            filters: { id: ids },
            limit: ITEMS_PER_PAGE,
            from,
            sort: {
              by: 'displayname',
              order: 'asc',
            },
          }
        )

        this.initialAccounts = [
          ...this.initialAccounts,
          ...(response?.data?.results ?? []),
        ]

        this.areThereMoreInitialResults =
          this.initialAccounts.length < response.data.totalRecordsInData
        if (this.areThereMoreInitialResults) {
          this.initialPageToLoad += 1
          this.getInitialAccounts()
        }
      } catch (error) {
        this.showError(error)
      } finally {
        this.$emit('loading', false)
      }
    },

    async searchItems(terms = '') {
      try {
        this.isLoading = true
        this.searchTerm = terms
        await Promise.all([this.searchAccounts(terms), this.searchTeams(terms)])
      } catch (err) {
        this.showError(err)
      } finally {
        this.isLoading = false
      }
    },

    async searchTeams(terms = '') {
      if (!this.includeTeams || this.localSearch) return
      const params =
        this.searchScope === this.$constants.SEARCH_SCOPES.site
          ? { filters: { sites: [this.account?.site?._id] }, terms }
          : { terms }

      const response = await api.teams.getTeamsStream(
        this.organizationIdToUse,
        {
          ...params,
          limit: ITEMS_PER_PAGE,
          sort: {
            by: terms?.length ? '_score' : 'name',
            order: terms?.length ? 'desc' : 'asc',
          },
        }
      )
      const fetchedTeams =
        response?.data?.results.map(result => result.team) ?? []

      this.availableTeams = fetchedTeams
    },

    async searchAccounts(terms = '') {
      if (this.localSearch) {
        return (this.availableAccounts = terms
          ? fuseSearch(this.localAccountsParsed, ['title'], terms)
          : this.localAccountsParsed)
      }

      const filters = { isDeleted: false }

      if (this.searchScope === this.$constants.SEARCH_SCOPES.site) {
        filters.site = this.account?.site?._id
      }

      const response = await api.account.getAccounts(this.organizationIdToUse, {
        filters: { ...filters, ...this.filters },
        terms,
        limit: ITEMS_PER_PAGE,
        sort: {
          by: terms?.length ? '_score' : 'displayname',
          order: terms?.length ? 'desc' : 'asc',
        },
      })
      this.availableAccounts = response?.data?.results ?? []
    },

    onSelect(element) {
      if (!this.multiple) {
        this.$emit('change', {
          ids: _.uniq([element._id]),
          items: [element],
        })

        this.open = false
        this.searchTerm = ''

        return
      }

      const userIds = element.isTeam
        ? this.getAccountIdsFromTeam(element)
        : [element._id]

      if (
        this.maxSelectableAccounts &&
        this.selectedAccountIds.length >= this.maxSelectableAccounts
      ) {
        this.showError(
          this.$t('common.selection_limit_error', {
            selectionLimit: this.maxSelectableAccounts,
            type: this.$t('common.users.user_other'),
          })
        )
      }

      if (this.maxSelectableAccounts) {
        return this.$emit('change', {
          ids: _.take(
            _.uniq([...this.selectedAccountIds, ...userIds]),
            this.maxSelectableAccounts
          ),
          items: _.take(this.selectedAccounts, this.maxSelectableAccounts),
        })
      }

      this.$emit('change', {
        ids: _.uniq([...this.selectedAccountIds, ...userIds]),
        items: this.selectedAccounts,
      })
    },

    handleSearchScopeChange(searchScope) {
      if (this.isLoading) return
      this.searchScope = searchScope
      this.searchItems(this.searchTerm)
    },

    getAccountIdsFromTeam(team) {
      const teamLeaders =
        team.leaders
          ?.filter(leader => !leader.isDeleted)
          ?.map(leader => leader.id) ?? []
      const teamMembers =
        team.members
          ?.filter(member => !member.isDeleted)
          ?.map(member => member.id) ?? []
      return [...teamLeaders, ...teamMembers]
    },

    parseAccounts(accounts) {
      if (!accounts) return

      return accounts?.map?.(account => ({
        ...account,
        id: account?._id ?? account?.id,
        photo: account?.photo,
        displayname: account?.displayname,
        familyname: account?.familyname,
        title: account?.title ?? this.getFullName(account),
        subtitle: account?.position,
      }))
    },

    parseTeams(teams) {
      if (!teams) return

      return teams?.map?.(team => {
        const totalMembers = this.getTeamTotalMembers(team)

        return {
          ...team,
          isTeam: true,
          title: team.name,
          subtitle: `${totalMembers} ${this.$t('common.member', {
            count: totalMembers,
          })}`,
        }
      })
    },

    getTeamTotalMembers(team) {
      return (team?.leaders?.length ?? 0) + (team?.members?.length ?? 0)
    },

    addAccountsToCache(accounts) {
      const parsedNewAccounts = this.parseAccounts(accounts)
      const hashedAccounts = _.keyBy(parsedNewAccounts, 'id')

      this.cachedAccounts = { ...this.cachedAccounts, ...hashedAccounts }
    },

    onRemove(item) {
      return this.$emit('change', {
        ids: this.selectedAccountIds.filter(
          accountId => accountId !== item._id
        ),
        items: this.selectedAccounts.filter(
          selectedAccount => selectedAccount._id !== item._id
        ),
      })
    },

    onClearAll() {
      return this.$emit('change', { ids: [], items: [] })
    },

    handleOpenAdvanceModal() {
      this.showAdvancedModal = true
      this.open = false
    },

    handleSelectFromAdvancedModal(selection) {
      this.showAdvancedModal = false
      this.$emit('change', selection || { ids: [], items: [] })
    },
  },
}
</script>

<style scoped></style>
